import { Link } from "react-router-dom";
import { Mail, Building2, Instagram } from "lucide-react";
import { useBrand } from "../contexts/BrandContext";

export default function Footer() {
  const brand = useBrand();
  const currentYear = new Date().getFullYear();

  const footerBg = brand.id === 'strider' ? 'bg-strider-charcoal' : 'bg-gray-900';

  return (
    <footer className={`${footerBg} text-gray-300 relative overflow-hidden`}>
      {/* Large Strider Logo Background */}
      <div 
        className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none"
        style={{
          width: '500px',
          height: '500px',
          backgroundImage: 'url(/strider-logo-bg.png)',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          transform: 'translateY(-50%) translateX(20%)'
        }}
      />
      
      <div className="max-w-7xl mx-auto px-4 py-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img 
                src={brand.logo} 
                alt={brand.name} 
                className="h-10 w-auto"
                style={{ background: 'transparent' }}
              />
              <span className="font-display font-semibold text-white text-xl tracking-tight">
                {brand.id === 'strider' ? 'STRIDER' : 'Work Away From Work'}
              </span>
            </Link>
            <p className="text-sm text-gray-400 max-w-sm">
              Find the perfect workspace wherever you are. Discover cafes, hotels, libraries, 
              and coworking spaces with {brand.id === 'strider' ? 'verified WiFi speeds' : 'great WiFi'} and power for remote work.
            </p>
            <div className="flex items-center gap-2 mt-4 text-sm">
              <Mail className="w-4 h-4 text-brand-light" />
              <a href={`mailto:${brand.email}`} className="hover:text-white transition-colors">
                {brand.email}
              </a>
            </div>
            {/* Social Links */}
            <div className="flex items-center gap-4 mt-4">
              {brand.social?.instagram && (
                <a 
                  href={brand.social.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-pink-400 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {brand.social?.facebook && (
                <a 
                  href={brand.social.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                  aria-label="Facebook"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display font-semibold text-white mb-4">Explore</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/browse" className="hover:text-white transition-colors">
                  Browse Locations
                </Link>
              </li>
              <li>
                <Link to="/add-location" className="hover:text-white transition-colors">
                  Add a Location
                </Link>
              </li>
              <li>
                <Link to="/claim" className="hover:text-white transition-colors">
                  Claim Your Venue
                </Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-white transition-colors">
                  About Strider
                </Link>
              </li>
              <li>
                <Link to="/community" className="hover:text-white transition-colors">
                  Community
                </Link>
              </li>
              <li>
                <Link to="/corporate" className="hover:text-white transition-colors flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5" />
                  Corporate Accounts
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-display font-semibold text-white mb-4">Resources</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/guides" className="hover:text-white transition-colors">
                  Remote Work Guides
                </Link>
              </li>
              <li>
                <Link to="/guides/wifi-requirements" className="hover:text-white transition-colors">
                  WiFi Speed Guide
                </Link>
              </li>
              <li>
                <Link to="/guides/workspace-etiquette" className="hover:text-white transition-colors">
                  Cafe Etiquette
                </Link>
              </li>
              <li>
                <Link to="/guides/security-public-wifi" className="hover:text-white transition-colors">
                  Security Tips
                </Link>
              </li>
              <li>
                <Link to="/blog" className="hover:text-white transition-colors">
                  Journal & Reports
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-display font-semibold text-white mb-4">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/terms" className="hover:text-white transition-colors">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-white transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="hover:text-white transition-colors">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link to="/contributor-terms" className="hover:text-white transition-colors">
                  Contributor Terms
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Big Tagline Section */}
        <div className="border-t border-gray-700 mt-8 pt-10 pb-6 text-center">
          <p className="text-2xl md:text-3xl lg:text-4xl font-display font-bold text-white">
            Let's expand where the world can succeed.
          </p>
        </div>

        {/* Popular Cities - SEO Internal Links */}
        <div className="border-t border-gray-700 pt-8">
          <h4 className="font-display font-semibold text-white mb-4">Popular Cities</h4>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <Link to="/city/london" className="hover:text-white transition-colors">London</Link>
            <Link to="/city/manchester" className="hover:text-white transition-colors">Manchester</Link>
            <Link to="/city/birmingham" className="hover:text-white transition-colors">Birmingham</Link>
            <Link to="/city/edinburgh" className="hover:text-white transition-colors">Edinburgh</Link>
            <Link to="/city/bristol" className="hover:text-white transition-colors">Bristol</Link>
            <Link to="/city/leeds" className="hover:text-white transition-colors">Leeds</Link>
            <Link to="/city/glasgow" className="hover:text-white transition-colors">Glasgow</Link>
            <Link to="/city/liverpool" className="hover:text-white transition-colors">Liverpool</Link>
            <Link to="/city/brighton" className="hover:text-white transition-colors">Brighton</Link>
            <Link to="/city/oxford" className="hover:text-white transition-colors">Oxford</Link>
            <Link to="/city/cambridge" className="hover:text-white transition-colors">Cambridge</Link>
            <Link to="/city" className="text-brand-light hover:text-white transition-colors">All Cities →</Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-500 text-center md:text-left">
            <p>© {currentYear} {brand.id === 'strider' ? 'Strider' : 'Work Away From Work'}. All rights reserved.</p>
            {brand.company && (
              <p className="text-xs text-gray-600 mt-1">
                {brand.company.name} · Company No. {brand.company.number}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <Link to="/terms" className="hover:text-gray-300 transition-colors">Terms</Link>
            <span>•</span>
            <Link to="/privacy" className="hover:text-gray-300 transition-colors">Privacy</Link>
            <span>•</span>
            <Link to="/cookies" className="hover:text-gray-300 transition-colors">Cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
